import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatPrice(amount: number, decimals: number = 2) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);
}

export function formatQuantityUnit(unit: string | undefined | null): string {
  if (!unit) return '';
  const cleaned = unit.trim().toLowerCase();
  
  if (cleaned === 'gram' || cleaned === 'grams' || cleaned === 'g') {
    return 'g';
  }
  if (cleaned === 'kilogram' || cleaned === 'kilograms' || cleaned === 'kg') {
    return 'kg';
  }
  if (cleaned === 'liter' || cleaned === 'liters' || cleaned === 'l') {
    return 'L';
  }
  if (cleaned === 'milliliter' || cleaned === 'milliliters' || cleaned === 'ml') {
    return 'ml';
  }
  if (cleaned === 'piece' || cleaned === 'pc') {
    return 'pc';
  }
  if (cleaned === 'pieces' || cleaned === 'pcs') {
    return 'pcs';
  }
  if (cleaned === 'packet' || cleaned === 'packets' || cleaned === 'pkt') {
    return 'pkt';
  }
  if (cleaned === 'bottle' || cleaned === 'bottles' || cleaned === 'btl') {
    return 'btl';
  }
  if (cleaned === 'box' || cleaned === 'boxes') {
    return 'box';
  }
  
  return cleaned;
}

export interface ImageEnhanceOptions {
  autoEnhance?: boolean;
  upscale?: boolean;
  brightness?: number; // 0.5 - 1.5
  contrast?: number;   // 0.5 - 1.5
  saturation?: number; // 0.5 - 2.0
  sharpness?: number;  // 0 - 100
  aspectPreset?: 'product' | 'banner' | 'whatsapp' | 'custom';
}

export interface CompressedImageResult {
  dataUrl: string;
  originalDataUrl?: string;
  format: 'avif' | 'webp' | 'jpeg' | 'png';
  originalSizeKb: number;
  compressedSizeKb: number;
  savingsPercent: number;
  dimensions?: { width: number; height: number };
}

export function compressImageToAvif(
  file: File | string,
  maxW = 1000,
  maxH = 1000,
  quality = 0.75,
  options: ImageEnhanceOptions = {}
): Promise<CompressedImageResult> {
  return new Promise((resolve, reject) => {
    const processImageSource = (srcUrl: string, originalFileSizeKb: number) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = srcUrl;
      img.onload = () => {
        let targetW = maxW;
        let targetH = maxH;

        // Apply 1-Click Preset dimensions if specified
        if (options.aspectPreset === 'product') {
          targetW = 500;
          targetH = 500;
        } else if (options.aspectPreset === 'banner') {
          targetW = 1200;
          targetH = 500;
        } else if (options.aspectPreset === 'whatsapp') {
          targetW = 800;
          targetH = 800;
        }

        let width = img.width;
        let height = img.height;

        // Apply 1.5x Upscaling if requested
        if (options.upscale) {
          width = Math.round(width * 1.5);
          height = Math.round(height * 1.5);
        }

        // Fit within target bounds while preserving proportion or fitting preset canvas
        if (options.aspectPreset && options.aspectPreset !== 'custom') {
          width = targetW;
          height = targetH;
        } else {
          if (width > height) {
            if (width > targetW) {
              height = Math.round((height * targetW) / width);
              width = targetW;
            }
          } else {
            if (height > targetH) {
              width = Math.round((width * targetH) / height);
              height = targetH;
            }
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });

        if (!ctx) {
          resolve({
            dataUrl: srcUrl,
            originalDataUrl: srcUrl,
            format: 'png',
            originalSizeKb: originalFileSizeKb,
            compressedSizeKb: originalFileSizeKb,
            savingsPercent: 0,
            dimensions: { width: img.width, height: img.height }
          });
          return;
        }

        // Apply Background color fill if needed for square product preset
        if (options.aspectPreset === 'product') {
          ctx.fillStyle = '#FFFFFF';
          ctx.fillRect(0, 0, width, height);
        }

        // Apply CSS Filters for live Brightness, Contrast, Saturation
        const brightness = options.brightness ?? (options.autoEnhance ? 1.08 : 1.0);
        const contrast = options.contrast ?? (options.autoEnhance ? 1.15 : 1.0);
        const saturation = options.saturation ?? (options.autoEnhance ? 1.12 : 1.0);

        ctx.filter = `brightness(${brightness}) contrast(${contrast}) saturate(${saturation})`;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        if (options.aspectPreset === 'product' || options.aspectPreset === 'whatsapp') {
          // Centered contain fit inside square canvas
          const scale = Math.min(width / img.width, height / img.height) * 0.92;
          const x = (width - img.width * scale) / 2;
          const y = (height - img.height * scale) / 2;
          ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
        } else if (options.aspectPreset === 'banner') {
          // Banner cover fit
          const scale = Math.max(width / img.width, height / img.height);
          const x = (width - img.width * scale) / 2;
          const y = (height - img.height * scale) / 2;
          ctx.drawImage(img, x, y, img.width * scale, img.height * scale);
        } else {
          ctx.drawImage(img, 0, 0, width, height);
        }

        // Reset filter
        ctx.filter = 'none';

        // Apply Sharpness filter kernel if requested
        if (options.sharpness && options.sharpness > 0) {
          try {
            const imgData = ctx.getImageData(0, 0, width, height);
            const d = imgData.data;
            const factor = (options.sharpness / 100) * 0.4;
            const w = width;

            // Simple 3x3 unsharp mask sharpening filter
            const copy = new Uint8ClampedArray(d);
            for (let y = 1; y < height - 1; y++) {
              for (let x = 1; x < width - 1; x++) {
                const i = (y * w + x) * 4;
                for (let c = 0; c < 3; c++) {
                  const center = copy[i + c];
                  const up = copy[((y - 1) * w + x) * 4 + c];
                  const down = copy[((y + 1) * w + x) * 4 + c];
                  const left = copy[(y * w + (x - 1)) * 4 + c];
                  const right = copy[(y * w + (x + 1)) * 4 + c];

                  const sharpVal = center + factor * (5 * center - (up + down + left + right));
                  d[i + c] = Math.min(255, Math.max(0, sharpVal));
                }
              }
            }
            ctx.putImageData(imgData, 0, 0);
          } catch (e) {
            console.warn('Sharpening process notice:', e);
          }
        }

        // Attempt output to AVIF format
        let dataUrl = canvas.toDataURL('image/avif', quality);
        let format: 'avif' | 'webp' | 'jpeg' | 'png' = 'avif';

        if (!dataUrl.startsWith('data:image/avif')) {
          // Fallback to WebP
          dataUrl = canvas.toDataURL('image/webp', quality);
          format = 'webp';

          if (!dataUrl.startsWith('data:image/webp')) {
            dataUrl = canvas.toDataURL('image/jpeg', quality);
            format = 'jpeg';
          }
        }

        const compressedSizeKb = (dataUrl.length * 0.75) / 1024;
        const savingsPercent = Math.max(0, Math.round((1 - (compressedSizeKb / originalFileSizeKb)) * 100));

        resolve({
          dataUrl,
          originalDataUrl: srcUrl,
          format,
          originalSizeKb: originalFileSizeKb,
          compressedSizeKb,
          savingsPercent,
          dimensions: { width, height }
        });
      };
      img.onerror = (err) => reject(err);
    };

    if (typeof file === 'string') {
      const estimatedSize = (file.length * 0.75) / 1024;
      processImageSource(file, estimatedSize);
    } else {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (e) => {
        const srcUrl = e.target?.result as string;
        processImageSource(srcUrl, file.size / 1024);
      };
      reader.onerror = (err) => reject(err);
    }
  });
}

export function compressImage(file: File, maxW = 1000, maxH = 1000, quality = 0.7): Promise<string> {
  return compressImageToAvif(file, maxW, maxH, quality).then((res) => res.dataUrl);
}

export function cleanCategoryName(categoryName: string | undefined | null): string {
  if (!categoryName) return '';
  return categoryName.split('(')[0].trim();
}

